import { Button } from "@/components/ui/button";
import { useState } from "react";
import { FileSearch, FileText, Loader2 } from "lucide-react";

import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Pager } from "@/components/ui/pager";
import { SkeletonRows } from "@/components/ui/skeleton";
import { useExamConfirm, useExamDiscard, useExamSuggested } from "@/hooks/use-exams";
import { ExamLabels } from "@/lib/labels/exams";
import { djs } from "@/lib/djs";

import { PersonSearch, patientIdOf, type ChosenPerson } from "./person-search";
import { MetaLine, QueueRow, QueueSection } from "./queue-section";

type Suggested = ReturnType<typeof useExamSuggested>["data"];
type SuggestedExam = NonNullable<Suggested>["items"][number];

function SuggestedThumbnail({ exam }: { exam: SuggestedExam }) {
	const mediaHref = `/media/${encodeURIComponent(exam.messageId)}`;

	if (!!exam.mime && exam.mime.startsWith("image/")) {
		return (
			<a href={mediaHref} target="_blank" rel="noreferrer" className="shrink-0">
				<img
					src={mediaHref}
					alt=""
					loading="lazy"
					className="size-14 rounded-md border border-border object-cover"
				/>
			</a>
		);
	}

	return (
		<a
			href={mediaHref}
			target="_blank"
			rel="noreferrer"
			className="flex size-14 shrink-0 items-center justify-center rounded-md border border-border bg-background/60 text-muted-foreground transition-colors hover:text-primary"
		>
			<FileText className="size-5" />
		</a>
	);
}

function SuggestedRow({ exam }: { exam: SuggestedExam }) {
	const confirm = useExamConfirm();
	const discard = useExamDiscard();

	const [chosen, setChosen] = useState<ChosenPerson | null>(null);

	const deciding = confirm.isPending || discard.isPending;
	const patientId = patientIdOf(chosen);

	return (
		<QueueRow>
			<div className="flex gap-3">
				<SuggestedThumbnail exam={exam} />

				<div className="flex min-w-0 flex-1 flex-col gap-2">
					<div className="flex flex-wrap items-center gap-2">
						<span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">
							{exam.personName ?? ExamLabels.suggested.unnamedContact}
						</span>

						<a
							href={`/media/${encodeURIComponent(exam.messageId)}`}
							target="_blank"
							rel="noreferrer"
							data-slot="open-document"
							className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
						>
							<FileText className="size-3" />
							{ExamLabels.suggested.open}
						</a>

						<ConfirmationDialog
							slot="discard-trigger"
							tone="destructive"
							title={ExamLabels.suggested.discardConfirm}
							pendingLabel={ExamLabels.suggested.discarding}
							isPending={discard.isPending}
							onConfirm={() => discard.mutate({ message_id: exam.messageId })}
							disabled={deciding}
							className="text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
						>
							{ExamLabels.suggested.discard}
						</ConfirmationDialog>
					</div>

					<MetaLine>
						{exam.reasons.length > 0 && (
							<span className="truncate">
								{exam.reasons.map((reason) => ExamLabels.scoreReason(reason)).join(", ")}
							</span>
						)}

						<span className="font-mono">{djs(exam.createdAt).format("DD/MM HH:mm")}</span>
					</MetaLine>

					<div className="flex flex-wrap items-center gap-2">
						<div className="min-w-48 flex-1">
							<PersonSearch chosen={chosen} onChoose={setChosen} disabled={deciding} />
						</div>

						<Button
							type="button"
							variant="default"
							size="sm"
							className="px-4"
							data-slot="register"
							disabled={deciding || patientId === null}
							onClick={() => {
								if (patientId !== null) {
									confirm.mutate({
										message_id: exam.messageId,
										patient_id: patientId,
									});
								}
							}}
						>
							{confirm.isPending && <Loader2 className="animate-spin" />}
							{confirm.isPending && "registrando exame..."}
							{!confirm.isPending && ExamLabels.suggested.register}
						</Button>
					</div>

					{chosen?.kind === "lead" && (
						<p data-slot="needs-patient" className="text-xs text-warning">
							{ExamLabels.suggested.needsPatient}
						</p>
					)}
				</div>
			</div>
		</QueueRow>
	);
}

export function ExamQueue({ page, onPage }: { page: number; onPage: (p: number) => void }) {
	const suggested = useExamSuggested(page);

	if (suggested.data === undefined) {
		return <SkeletonRows rows={2} />;
	}

	return (
		<div data-component="exam-queue" className="flex flex-col gap-4">
			<QueueSection title={ExamLabels.suggested.title} subtitle={ExamLabels.suggested.subtitle}>
				{suggested.data.items.length === 0 && (
					<EmptyState
						icon={FileSearch}
						title={ExamLabels.suggested.emptyTitle}
						description={ExamLabels.suggested.emptyDescription}
					/>
				)}

				{suggested.data.items.map((exam) => (
					<SuggestedRow key={exam.messageId} exam={exam} />
				))}
			</QueueSection>

			{suggested.data.pages > 1 && (
				<Pager page={page} pages={suggested.data.pages} onPage={onPage} />
			)}
		</div>
	);
}
